<script setup lang="ts">
import { FEMediaItem } from "@/models/frontend-only/FEMediaItem";

const store = useSongStore();
const youtube = useYoutubeStore();
const mediaStore = useMediaPlayerStore();
const lyricVideoMVPStore = useLyricVideoMVPStore();

const YoutubePlayer = defineAsyncComponent(
  () => import("@/components/YouTubePlayer.vue"),
);

const enableLyricVideoMVP = computed(() => lyricVideoMVPStore.enable);

const mediaItems = computed(() => {
  if (store.dataType === DataType.Arrangement) {
    return store.arrangement?.alternateArrangements ?? null;
  } else {
    const media = store.media ?? null;
    if (!enableLyricVideoMVP.value) {
      return media;
    } else {
      return media.filter((m) => m.type !== "video");
    }
  }
});

function playItem(id: string) {
  id === mediaStore.nowPlayingId
    ? mediaStore.togglePlay()
    : mediaStore.playMediaItem(id);
  window.scrollTo(0, 0);
}

function onSelectItem(m: FEMediaItem) {
  playItem(m.id);
}

onMounted(() => {
  mediaStore.reset();
});
</script>

<template>
  <MediaSlider
    v-if="mediaItems"
    class="slider"
    dark
  >
    <MediaSliderItem
      v-for="(m, i) of mediaItems"
      :key="i"
      class="media-item"
    >
      <button
        type="button"
        class="media-item-button ga-media-item-button"
        :class="{
          active: m.id === mediaStore.nowPlayingId,
          'media-item-button-video': m.type === 'video',
          'media-item-button-audio': m.type !== 'video',
        }"
        :data-song-number="store.song?.ccliSongNumber"
        :data-song-media-id="m.id"
        @click="onSelectItem(m as FEMediaItem)"
      >
        <div
          class="media-item-artwork"
          :class="{
            'media-item-artwork-video': m.type === 'video',
            'media-item-artwork-audio': m.type !== 'video',
          }"
        >
          <AlbumArtwork
            :url="m.artwork || ''"
            :media-type="m.type === 'video' ? 'video' : 'song'"
            :show-status="true"
            :playing="
              (m.id === mediaStore.nowPlayingId &&
                mediaStore.nowPlaying &&
                mediaStore.status === MediaPlayerStatus.Playing) ||
              false
            "
            :restrict-width="m.type === 'video' ? 213 : 120"
          />

          <div class="youtube-wrapper">
            <YoutubePlayer
              v-if="
                m.id === mediaStore.nowPlayingId &&
                m.type === 'video' &&
                mediaStore.getYoutubeUrl(m)
              "
              :class="youtube.isFullScreen ? 'hidden' : 'block'"
              :src="mediaStore.getYoutubeUrl(m)!"
            />
          </div>

          <div
            v-if="m.id !== mediaStore.nowPlayingId"
            class="media-item-artwork-focus"
          >
            <FAIcon class="fa-solid fa-play fa-2x" />
          </div>
        </div>

        <div class="after-artwork">
          <div
            class="labels larger-maxwidth"
            :class="{ 'video-label': m.type === 'video' }"
          >
            <div
              class="title"
              :title="m.title"
              :class="{ 'asian-text': m.title && hasAsianText(m.title) }"
            >
              {{ m.title }}
            </div>
            <div
              class="subtitle"
              :title="m.subtitle"
              :class="{ 'asian-text': m.subtitle && hasAsianText(m.subtitle) }"
            >
              {{ m.subtitle }}
            </div>
          </div>
        </div>
      </button>
    </MediaSliderItem>
  </MediaSlider>
</template>

<style scoped lang="postcss">
.media-item {
  --thumbnail-height: 7.5em;
  --thumbnail-width-video: calc(var(--thumbnail-height) * 16 / 9);
  --thumbnail-width: var(--thumbnail-height);
}
.media-item-artwork-video {
  height: var(--thumbnail-height);
  width: var(--thumbnail-width-video);
}
.media-item-artwork-audio {
  height: var(--thumbnail-height);
  width: var(--thumbnail-width);
}
.media-item-button {
}
.media-item-artwork {
  @apply relative;
}
.media-item-artwork-focus {
  @apply absolute bottom-0 left-0 right-0 top-0 flex items-center justify-center bg-black bg-opacity-25 opacity-0 transition-all;
}
.media-item-button {
  &:hover,
  &:focus-visible {
    .media-item-artwork-focus {
      @apply opacity-100;
    }
    :deep() {
      .content.empty {
        i,
        svg {
          @apply opacity-0;
        }
      }
    }
  }
}
.media-item-button-video {
  width: var(--thumbnail-width-video);
}

.title {
  @apply line-clamp-1 leading-5;
  &:not(.asian-text) {
    padding-bottom: 0.14em;
  }
}
.subtitle {
  @apply line-clamp-1 text-sm leading-5;
  &:not(.asian-text) {
    padding-bottom: 0.14em;
  }
}
.title,
.subtitle {
  max-width: var(--thumbnail-width);
}
.after-artwork {
  @apply py-1 text-left;
}
.youtube-wrapper {
  @apply absolute bottom-0 left-0 right-0 top-0 overflow-hidden rounded-lg;
}
</style>
